/** Appearance model: two INDEPENDENT choices —
 *   1. color mode: light | dark | system (system follows the OS)
 *   2. accent PER MODE: light and dark each hold their OWN accent (accentLight /
 *      accentDark), so they can be completely different colours and each mode
 *      remembers its own. Both default to the brand emerald — rendered mode-
 *      tuned (deep #10805c on light, bright #3ecf8e on dark).
 *
 *  There are no bundled "presets": surfaces come from one light token set
 *  (theme.css @theme defaults) and one dark set ([data-mode="dark"]); the accent
 *  for the ACTIVE mode recolors action/highlight. Picking a colour changes only
 *  the mode you're currently in. Each accent still resolves to a mode-tuned
 *  shade (see resolveAccent). */

export type ColorMode = "light" | "dark" | "system";
export type ResolvedMode = "light" | "dark";

export interface AccentSwatch {
  name: string;
  /** Stable identity: the value stored in localStorage / synced to the server,
   *  the picker's swatch dot, and the key `resolveAccent` looks a swatch up by.
   *  Kept === the pre-2026-07 single hex so existing stored selections still
   *  validate. NOT necessarily what gets stamped — that's the per-mode value. */
  hex: string;
  /** The colour actually stamped onto --color-accent per mode. Every accent is
   *  PER-MODE: a deeper shade that keeps contrast on the white light surface,
   *  and a brighter sibling of the same hue that pops on dark graphite. Dark
   *  values are tuned at/below the emerald's brightness so white-on-accent text
   *  (filled buttons, avatars) stays legible. See resolveAccent. */
  light: string;
  dark: string;
}

/** Accent swatches. Emerald is the brand colour and the default for BOTH modes.
 *  The rest are accent-only. Each swatch is PER-MODE — `light`/`dark` are tuned
 *  so it reads cleanly on white and on graphite. `hex` is the stored identity. */
export const ACCENTS: AccentSwatch[] = [
  { name: "Emerald", hex: "#3ecf8e", light: "#10805c", dark: "#3ecf8e" },
  { name: "Teal", hex: "#006491", light: "#006491", dark: "#38a8d6" },
  { name: "Caramel", hex: "#cd8e54", light: "#cd8e54", dark: "#dda86c" },
  { name: "Violet", hex: "#6a4c93", light: "#6a4c93", dark: "#a98bd6" },
  { name: "Blue", hex: "#2f5e9e", light: "#2f5e9e", dark: "#6c9ce6" },
  { name: "Orange", hex: "#c2641e", light: "#c2641e", dark: "#eb9042" },
  { name: "Rose", hex: "#d94f6c", light: "#d94f6c", dark: "#ec7d95" },
  { name: "Magenta", hex: "#b34aa0", light: "#b34aa0", dark: "#d47ec6" },
  { name: "Indigo", hex: "#5b63d6", light: "#5b63d6", dark: "#8f95ea" },
  { name: "Slate", hex: "#5f6b7a", light: "#5f6b7a", dark: "#98a4b4" },
];

/** CSS variables the accent rewrites (action + highlight color), stamped inline
 *  on <html> with the per-mode value from resolveAccent(). */
export const ACCENT_VARS = ["--color-accent", "--color-nav-icon", "--color-user-bubble"];

/** CSS variables the snippet-driven surface palette rewrites (embed only).
 *  Grouped so AppearanceProvider and the embed.html pre-paint stamp map the same
 *  brand field to the same vars. Unlike the accent (one colour → per-mode
 *  shades), these are ABSOLUTE: the value is applied verbatim to whichever mode
 *  is active, so an integrator who overrides surfaces should also pin the mode. */
export const BRAND_BG_VARS = ["--color-canvas"];
/** The sidebar/nav background — a separate knob from the page background so the
 *  nav can stay visually distinct from the chat panel (or match it). */
export const BRAND_SIDEBAR_VARS = ["--color-sidebar"];
/** Sidebar-scoped ink ramp — independent from page ink so a dark branded
 *  sidebar can render white icons/text while the chat panel stays as-is. */
export const BRAND_SIDEBAR_INK_RAMP = [
  { var: "--color-sidebar-ink", mix: 0 },
  { var: "--color-sidebar-ink-secondary", mix: 0.3 },
  { var: "--color-sidebar-ink-muted", mix: 0.5 },
  { var: "--color-sidebar-ink-faint", mix: 0.64 },
];
export const BRAND_SIDEBAR_NAV_ICON_VAR = "--color-sidebar-nav-icon";
export const BRAND_SIDEBAR_HOVER_VAR = "--color-sidebar-hover";
export const BRAND_SIDEBAR_NAV_ACTIVE_VAR = "--color-sidebar-nav-active";
/** The whole ink ramp — primary + the three de-emphasised shades — so a
 *  snippet text colour recolours ALL text, not just headings/body. Each shade
 *  fades the chosen colour toward the background by `mix` to preserve the
 *  primary→secondary→muted→faint hierarchy (mirrors the default grey ramp). */
export const BRAND_TEXT_RAMP = [
  { var: "--color-ink", mix: 0 },
  { var: "--color-ink-secondary", mix: 0.3 },
  { var: "--color-ink-muted", mix: 0.5 },
  { var: "--color-ink-faint", mix: 0.64 },
];
/** Just the primary text var (kept for callers that only need the base ink). */
export const BRAND_TEXT_VARS = [BRAND_TEXT_RAMP[0].var];

/** Mirror of theme.css --color-canvas per mode. Used as the fade target for the
 *  derived ink ramp when the snippet sets a text colour but no background. Keep
 *  in sync with theme.css. */
export const CANVAS_BY_MODE: Record<ResolvedMode, string> = {
  light: "#f6f7f8",
  dark: "#121316",
};

export const DEFAULT_ACCENT = "#3ecf8e";
/** Per-mode brand emerald — mirrors the Emerald swatch's light/dark pair.
 *  Derived (not hand-typed) so it can't drift from ACCENTS; keep in sync with
 *  the --color-accent fallback in theme.css and the index.html pre-paint map. */
const EMERALD = ACCENTS.find((a) => a.hex === DEFAULT_ACCENT)!;
export const BRAND_ACCENT: Record<ResolvedMode, string> = { light: EMERALD.light, dark: EMERALD.dark };

/** Per-mode DEFAULT accents. Light and dark start on different, unrelated
 *  colours and each mode remembers its own selection. Keep the values in sync
 *  with the pre-paint defaults in index.html. */
export const DEFAULT_ACCENT_LIGHT = DEFAULT_ACCENT; // Emerald → #10805c on light
export const DEFAULT_ACCENT_DARK = DEFAULT_ACCENT;  // Emerald → #3ecf8e on dark

/** The concrete accent to stamp: every swatch resolves to its per-mode value
 *  (deeper on light, brighter on dark). An unknown hex is stamped verbatim as a
 *  defensive fallback — the picker only ever passes an allowlisted identity. */
export function resolveAccent(accent: string, resolved: ResolvedMode): string {
  const swatch = ACCENTS.find((a) => a.hex === accent);
  return swatch ? swatch[resolved] : accent;
}

/** The accent identity in play for the resolved mode — dark uses accentDark,
 *  light uses accentLight. Feed the result to resolveAccent() to get the shade. */
export function accentForMode(sel: AppearanceSelection, resolved: ResolvedMode): string {
  return resolved === "dark" ? sel.accentDark : sel.accentLight;
}

// ── Brand colour → per-mode shades ───────────────────────────────────────────
// Used by the embed to turn ONE company brand hex into a { light, dark } pair.

function hexToRgb(hex: string): [number, number, number] {
  let h = hex.slice(1);
  if (h.length === 3) h = [...h].map((c) => c + c).join("");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function rgbToHex(r: number, g: number, b: number): string {
  const to2 = (n: number) =>
    Math.round(Math.min(255, Math.max(0, n))).toString(16).padStart(2, "0");
  return `#${to2(r)}${to2(g)}${to2(b)}`;
}

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  if (d === 0) return [0, 0, l];
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return [h / 6, s, l];
}

function hslToRgb(h: number, s: number, l: number): [number, number, number] {
  if (s === 0) { const v = l * 255; return [v, v, v]; }
  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return [
    hue2rgb(p, q, h + 1 / 3) * 255,
    hue2rgb(p, q, h) * 255,
    hue2rgb(p, q, h - 1 / 3) * 255,
  ];
}

/** One brand colour → a { light, dark } accent pair (the "one colour, auto-
 *  derive" model). `light` is the colour as chosen (brands are picked against
 *  light surfaces); `dark` lifts it to a brighter, still-saturated shade that
 *  reads on the graphite dark surface — mirroring the built-in swatches, whose
 *  dark shades sit around L 0.5–0.6. Deterministic + pure. Invalid input falls
 *  back to the brand emerald pair (callers should guard with isHexColor). */
export function deriveBrandShades(hex: string): { light: string; dark: string } {
  if (!isHexColor(hex)) return { light: BRAND_ACCENT.light, dark: BRAND_ACCENT.dark };
  const [r, g, b] = hexToRgb(hex);
  const [h, s, l] = rgbToHsl(r, g, b);
  const [dr, dg, db] = hslToRgb(h, Math.max(s, 0.35), Math.max(l, 0.58));
  return { light: rgbToHex(r, g, b), dark: rgbToHex(dr, dg, db) };
}

// ── Snippet-driven surface palette (embed) ───────────────────────────────────
// Background / surface / text overrides from the host embed snippet. Absolute
// colours applied straight onto CSS vars (not per-mode-derived like the accent).

/** WCAG relative luminance (0..1) of a hex colour — used to pick readable ink. */
function luminance(hex: string): number {
  const chan = (c: number) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
  };
  const [r, g, b] = hexToRgb(hex);
  return 0.2126 * chan(r) + 0.7152 * chan(g) + 0.0722 * chan(b);
}

/** Readable ink (near-black / white) for a background, by luminance. */
export function readableInk(bgHex: string): string {
  return luminance(bgHex) > 0.5 ? "#111111" : "#ffffff";
}

/** Linear blend of two hex colours (`t` in 0..1: 0 → a, 1 → b). */
function mixHex(a: string, b: string, t: number): string {
  const [ar, ag, ab] = hexToRgb(a);
  const [br, bg, bb] = hexToRgb(b);
  return rgbToHex(ar + (br - ar) * t, ag + (bg - ag) * t, ab + (bb - ab) * t);
}

/** Stamp the WHOLE ink ramp from one text colour, fading toward `base` (the
 *  background the text sits on) so primary→muted hierarchy survives. */
export function stampInkRamp(el: HTMLElement, ink: string, base: string): void {
  for (const { var: v, mix } of BRAND_TEXT_RAMP) {
    el.style.setProperty(v, mix === 0 ? ink : mixHex(ink, base, mix));
  }
}

/** Stamp sidebar text/icon tokens from a readable ink colour and the sidebar
 *  background. Nav icons follow ink (not accent) on custom sidebars. */
export function stampSidebarInkRamp(el: HTMLElement, ink: string, sidebarBg: string): void {
  for (const { var: v, mix } of BRAND_SIDEBAR_INK_RAMP) {
    el.style.setProperty(v, mix === 0 ? ink : mixHex(ink, sidebarBg, mix));
  }
  el.style.setProperty(BRAND_SIDEBAR_NAV_ICON_VAR, ink);
  const dark = luminance(sidebarBg) <= 0.5;
  el.style.setProperty(
    BRAND_SIDEBAR_HOVER_VAR,
    dark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)",
  );
  el.style.setProperty(
    BRAND_SIDEBAR_NAV_ACTIVE_VAR,
    dark ? "rgba(255, 255, 255, 0.12)" : "#ffffff",
  );
}

/** CSS variables the app marks (Aster's trefoil, Bolt's face) paint from. They
 *  are UNSET by default so the marks fall back to the fixed Artie green; only a
 *  host that declares its own brand takes them over. */
export const BRAND_MARK_VARS = {
  petal: "--brand-mark-petal",
  ring: "--brand-mark-ring",
  face: "--brand-mark-face",
  feature: "--brand-mark-feature",
  // The sidebar copy of the trefoil is tracked separately: it sits on the
  // host's sidebar colour, which is often the accent itself.
  sidebarPetal: "--brand-mark-sidebar-petal",
  sidebarRing: "--brand-mark-sidebar-ring",
  sidebarAperture: "--brand-mark-sidebar-aperture",
} as const;

/** Recolour the app marks to the host's accent (embed only).
 *
 *  Without this the trefoil and Bolt stay Artie-green inside someone else's
 *  product, which reads as a third-party widget bolted on rather than part of
 *  the host application. Ring and facial features are darkened off the accent
 *  rather than taken from a token, because they have to stay legible AGAINST
 *  the accent they sit on — at 24px an aperture or a pupil that matches its
 *  body just disappears. Hex-only: the value goes straight into a custom
 *  property, so the guard is the CSS-injection defence. */
export function stampBrandMark(
  el: HTMLElement,
  brandAccent?: string,
  brandSidebar?: string,
): void {
  if (!isHexColor(brandAccent)) return;
  el.style.setProperty(BRAND_MARK_VARS.petal, brandAccent);
  el.style.setProperty(BRAND_MARK_VARS.face, brandAccent);
  el.style.setProperty(BRAND_MARK_VARS.ring, mixHex(brandAccent, "#000000", 0.3));
  el.style.setProperty(BRAND_MARK_VARS.feature, mixHex(brandAccent, "#000000", 0.62));

  // Hosts commonly paint the sidebar in their accent, which would leave an
  // accent trefoil invisible on it. Reverse the mark out of the sidebar instead
  // and knock its aperture through to the surface — the same treatment the
  // `onAccent` variant uses for a solid brand tile.
  const onSidebar = isHexColor(brandSidebar) ? readableInk(brandSidebar) : brandAccent;
  el.style.setProperty(BRAND_MARK_VARS.sidebarPetal, onSidebar);
  el.style.setProperty(BRAND_MARK_VARS.sidebarRing, onSidebar);
  el.style.setProperty(
    BRAND_MARK_VARS.sidebarAperture,
    isHexColor(brandSidebar) ? brandSidebar : "#ffffff",
  );
}

/** Stamp the snippet-driven surface palette onto an element's inline style
 *  (embed only). Each field applies only when it's a valid hex — the value goes
 *  straight into a CSS custom property, so the isHexColor guard is the CSS-
 *  injection defence. The text colour recolours the ENTIRE ink ramp (all text),
 *  faded toward the background for hierarchy. When `text` is omitted but a
 *  background IS set, the ink is auto-derived for contrast so a custom dark
 *  background never renders unreadable dark text. Inline style wins over the
 *  light/dark token sheet, so a single stamp holds across mode toggles. The
 *  `fadeTarget` is the active mode's canvas (see CANVAS_BY_MODE), used to fade
 *  the ramp when only a text colour is given. */
export function stampBrandSurfaces(
  el: HTMLElement,
  brand: { bg?: string; sidebar?: string; text?: string },
  fadeTarget: string = "#ffffff",
): void {
  const set = (vars: string[], hex: string) => {
    for (const v of vars) el.style.setProperty(v, hex);
  };
  if (isHexColor(brand.bg)) set(BRAND_BG_VARS, brand.bg);
  if (isHexColor(brand.sidebar)) {
    set(BRAND_SIDEBAR_VARS, brand.sidebar);
    const sidebarInk = isHexColor(brand.text) ? brand.text : readableInk(brand.sidebar);
    stampSidebarInkRamp(el, sidebarInk, brand.sidebar);
  }

  // Text sits on the page background (else the mode's canvas).
  const base = isHexColor(brand.bg) ? brand.bg : fadeTarget;
  const ink = isHexColor(brand.text)
    ? brand.text
    : isHexColor(brand.bg)
      ? readableInk(base) // no explicit text, but a custom bg → auto-contrast
      : undefined; // nothing to base a colour on → leave the token ramp
  if (ink) stampInkRamp(el, ink, base);
}

export const DEFAULT_MODE: ColorMode = "system";
export const STORAGE_KEY = "artie-appearance";
export const LEGACY_MODE_KEY = "artie_theme_mode";

/** Where a surface keeps its appearance selection.
 *
 *  The embed is served from the same origin as the SPA, so they share one
 *  localStorage. A preference the viewer set in the SPA says nothing about how
 *  a host wants its embedded assistant branded — left on the same key it would
 *  silently outrank the host's brand and the embed would keep painting Artie
 *  green inside someone else's product.
 *
 *  Scoping by brand also means a host changing its colours retires any tweak
 *  stored against the previous one, instead of pinning the old company colour
 *  forever. */
export function appearanceStorageKey(brandAccent?: string): string {
  if (!isHexColor(brandAccent)) return STORAGE_KEY;
  return `${STORAGE_KEY}:embed:${brandAccent.toLowerCase()}`;
}

export interface AppearanceSelection {
  mode: ColorMode;
  /** Accent shown in LIGHT mode (swatch identity). */
  accentLight: string;
  /** Accent shown in DARK mode (swatch identity) — independent of accentLight. */
  accentDark: string;
}

/** Factory (not a shared constant) so callers can't mutate a singleton. */
export function defaultSelection(): AppearanceSelection {
  return { mode: DEFAULT_MODE, accentLight: DEFAULT_ACCENT_LIGHT, accentDark: DEFAULT_ACCENT_DARK };
}

/** Legacy preset ids that were dark (for migrating pre-2026-07 stored state). */
const LEGACY_DARK_PRESETS = new Set(["editorial", "midnight", "onyxsage"]);

function isMode(v: unknown): v is ColorMode {
  return v === "light" || v === "dark" || v === "system";
}
/** A stored accent must be a hex colour — a swatch identity OR a company/viewer
 *  brand hex. The value is stamped straight into a CSS custom property, so the
 *  strict `#rgb`/`#rrggbb` shape is load-bearing: it rejects anything that could
 *  inject CSS (e.g. "url(x)", "red; …"). This widens the old swatch-only
 *  allowlist to any valid hex so custom brand colours survive a reload, without
 *  weakening the injection guard. */
export function isHexColor(v: unknown): v is string {
  return typeof v === "string" && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(v);
}

/** Turn a color-mode choice into the concrete light/dark surface set. */
export function resolveMode(mode: ColorMode, prefersDark: boolean): ResolvedMode {
  if (mode === "system") return prefersDark ? "dark" : "light";
  return mode;
}

/** Migrate a single pre-per-mode accent to a { light, dark } pair: a custom
 *  accent applied to BOTH modes, so keep it for both; the plain emerald default
 *  means "unset", so dark falls to its own default (blue). Shared by the
 *  localStorage read and the server-sync path so they agree. */
export function splitLegacyAccent(single: string): { accentLight: string; accentDark: string } {
  // A pre-per-mode accent applied to both modes; keep it that way. (Emerald, the
  // old default, maps to emerald/emerald — which is also the new default.)
  return { accentLight: single, accentDark: single };
}

export function readStoredAppearance(key: string = STORAGE_KEY): AppearanceSelection {
  let raw: Record<string, unknown> | null = null;
  try {
    raw = JSON.parse(localStorage.getItem(key) ?? "null");
  } catch { /* corrupted → defaults */ }

  if (raw) {
    const mode = isMode(raw.mode) ? raw.mode : DEFAULT_MODE;

    // Current shape: independent per-mode accents.
    if (raw.accentLight !== undefined || raw.accentDark !== undefined) {
      return {
        mode,
        accentLight: isHexColor(raw.accentLight) ? raw.accentLight : DEFAULT_ACCENT_LIGHT,
        accentDark: isHexColor(raw.accentDark) ? raw.accentDark : DEFAULT_ACCENT_DARK,
      };
    }
    // Migrate the pre-2026-07 preset shape { themeId, accent? } — it carries the
    // mode via the preset id, and may also carry a single accent.
    if (typeof raw.themeId === "string") {
      const single = isHexColor(raw.accent) ? raw.accent : DEFAULT_ACCENT;
      return {
        mode: LEGACY_DARK_PRESETS.has(raw.themeId) ? "dark" : "light",
        ...splitLegacyAccent(single),
      };
    }
    // Migrate the single-accent shape { mode?, accent }.
    if (isHexColor(raw.accent)) {
      return { mode, ...splitLegacyAccent(raw.accent) };
    }
    // A valid mode alone → keep it, default the accents.
    if (isMode(raw.mode)) return { ...defaultSelection(), mode: raw.mode };
  }

  // Migrate the original boolean light/dark key.
  try {
    const legacy = localStorage.getItem(LEGACY_MODE_KEY);
    if (legacy === "dark") return { ...defaultSelection(), mode: "dark" };
    if (legacy === "light") return { ...defaultSelection(), mode: "light" };
  } catch { /* private mode */ }

  return defaultSelection();
}

export function writeStoredAppearance(
  sel: AppearanceSelection,
  key: string = STORAGE_KEY,
): void {
  try {
    localStorage.setItem(key, JSON.stringify(sel));
  } catch { /* private mode: session-only */ }
}
